import React, { MouseEventHandler, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Pane from 'components/Common/Pane'
import useToggle from 'hooks/useToggle'
import { take } from 'lib/array/arrayUtils'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import FlashHotelRateOption from './FlashHotelRateOption'
import SmallToggleButton from 'components/Luxkit/Button/SmallToggleButton'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterTypes } from 'contexts/OfferPage/offerPageStateReducer'
import { connect } from 'react-redux'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import AccommodationRoomTile from '../Accommodation/AccommodationRoomTile'
import AccommodationRoomTileDetails from '../Accommodation/AccommodationRoomTileDetails'
import AccommodationRoomTileImage from '../Accommodation/AccommodationRoomTileImage'
import AccommodationRoomTileActions, { AccommodationRoomTileActionsState } from '../Accommodation/AccommodationRoomTileActions'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { getOnlyNRoomLeftLabel } from 'lib/offer/getUrgencyLabels'
import config from 'constants/config'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'

const RateSection = styled.div`
  > * {
    border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  }
`

const ShowMoreContent = styled(SmallToggleButton)`
  padding: ${rem(12)};
  width: 100%;
  justify-content: center;
`

interface Props {
  offer: App.Offer;
  packageViews: Array<OfferPackageOptionView>;
  flightDestination?: App.Airport;
  previewMode: boolean;
}

const initialRatesToShow = 4

function FlashHotelRoomOption(props: Props) {
  const {
    offer,
    packageViews,
    flightDestination,
    previewMode,
  } = props

  const pkg = packageViews[0].package
  const [showingMoreRates, showMoreRates] = useToggle()

  const [roomFitsCapacity, datesAvailable] = useMemo(() => [
    packageViews.some(view => view.capacityValid),
    packageViews.some(view => view.hotelAvailable),
  ], [packageViews])

  const availablePackageViews = useMemo(() => packageViews.filter(view => view.capacityValid && view.hotelAvailable), [packageViews])
  const availableForPurchase = useIsAvailableForPurchase()
  /**
   * Preview is used only by content people.
   * We don't want to show unavailable rateOptions to the user.
   */
  const rateOptionViews = previewMode ? packageViews : availablePackageViews

  const actionsState = useMemo<AccommodationRoomTileActionsState | undefined>(() => {
    if (availableForPurchase) {
      if (!roomFitsCapacity && datesAvailable) return 'exceeded-capacity'
      if (!datesAvailable) return 'sold-out-on-dates'
    }
  }, [availableForPurchase, datesAvailable, roomFitsCapacity])

  const offerPageDispatch = useOfferPageDispatch()

  const toggleFilter = useCallback((filter: OfferPageFilterTypes) => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter })
  }, [offerPageDispatch, pkg.roomType])

  const handleChangeDatesClick = useCallback<MouseEventHandler<HTMLButtonElement>>(() => {
    toggleFilter('checkin')
  }, [toggleFilter])

  const handleAddAnotherUnitClick = useCallback<MouseEventHandler<HTMLButtonElement>>(() => {
    toggleFilter('capacity')
  }, [toggleFilter])

  const roomsLeftLabel = useMemo(() => getOnlyNRoomLeftLabel(packageViews[0].availableRooms ?? 0, true, config.RATE_OPTION_ONLY_N_ROOMS_LEFT_THRESHOLD), [packageViews])
  if (!pkg.roomType) { return null }

  return (
    <Pane bordered data-testid="package-option">
      <ScrollAnchor id={pkg.roomType.id} vOffset={rem(80)} />
      <AccommodationRoomTile roomPackage={pkg as App.HotelPackage}>
        <AccommodationRoomTileImage
          // @ts-expect-error not HotelPackage
          roomPackage={pkg}
        />
        <AccommodationRoomTileDetails
          // @ts-expect-error not HotelPackage
          roomPackage={pkg}
          urgencyLabels={<>
            {!datesAvailable && <UrgencyLabel variant="critical2">SOLD OUT</UrgencyLabel>}
            <BookingsCountLabel bookings={pkg.roomType.stats.booked} threshold="room" />
            {roomsLeftLabel && <UrgencyLabel variant="critical2">{roomsLeftLabel}</UrgencyLabel>}
          </>}
          actions={availableForPurchase && datesAvailable && roomFitsCapacity && <CSSBreakpoint min="desktop">
            <TripGuard>
              <BookmarkButton offer={offer} roomTypeId={pkg.roomType.id} />
            </TripGuard>
          </CSSBreakpoint>}
        >
          <AccommodationRoomTileActions
            // @ts-expect-error not HotelPackage
            roomPackage={pkg}
            state={actionsState}
            onAddAnotherUnitClick={handleAddAnotherUnitClick}
            onChangeDatesClick={handleChangeDatesClick}
          />
        </AccommodationRoomTileDetails>
      </AccommodationRoomTile>
      <RateSection>
        {take(rateOptionViews, showingMoreRates ? 100 : initialRatesToShow).map((view, index) => <FlashHotelRateOption
          key={view.package.uniqueKey}
          offer={offer}
          optionNumber={index + 1}
          packageView={view}
          flightDestination={flightDestination}
        />)}
        {rateOptionViews.length > initialRatesToShow &&
          <ShowMoreContent
            on={showingMoreRates}
            onClick={showMoreRates}
            size="small"
          >
            {showingMoreRates ? 'Show less options' : 'Show more options'}
          </ShowMoreContent>
        }
      </RateSection>
    </Pane>
  )
}

function mapStateToProps(state: App.State) {
  return {
    previewMode: state.system.previewMode,
  }
}

export default connect(mapStateToProps)(FlashHotelRoomOption)
